import { useCallback } from 'react'
import { Button, Col, DatePicker, Row, Select } from 'antd'
import { ClearOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import dayjs, { type Dayjs } from 'dayjs'

import type { SettlementStatusRoot } from '@repo/sdk'

import { useSettlementStatusConfig } from './settlementStatusConfig'

const { RangePicker } = DatePicker

const SETTLEMENT_STATUSES: SettlementStatusRoot[] = [
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'BLOCKED',
]

interface SettlementHistoryFiltersProps {
  status: SettlementStatusRoot | ''
  dateFrom: string
  dateTo: string
  onStatusChange: (status: SettlementStatusRoot | '') => void
  onDateRangeChange: (dateFrom: string, dateTo: string) => void
  onReset: () => void
}

export function SettlementHistoryFilters({
  status,
  dateFrom,
  dateTo,
  onStatusChange,
  onDateRangeChange,
  onReset,
}: SettlementHistoryFiltersProps) {
  const { t } = useTranslation()
  const statusConfig = useSettlementStatusConfig()

  const statusOptions = [
    { value: '', label: t('pages.settlements.filters.allStatuses') },
    ...SETTLEMENT_STATUSES.map((s) => ({ value: s, label: statusConfig[s].label })),
  ]

  const rangeValue: [Dayjs, Dayjs] | null =
    dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : null

  const handleRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (dates?.[0] && dates[1]) {
        onDateRangeChange(dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD'))
      } else {
        onDateRangeChange('', '')
      }
    },
    [onDateRangeChange],
  )

  const hasActiveFilters = status !== '' || dateFrom !== '' || dateTo !== ''

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} md={6}>
        <Select
          data-testid="settlement-history-filter-status"
          value={status}
          onChange={onStatusChange}
          options={statusOptions}
          style={{ width: '100%' }}
          allowClear={false}
        />
      </Col>
      <Col xs={24} sm={12} md={8}>
        <RangePicker
          data-testid="settlement-history-filter-date-range"
          value={rangeValue}
          onChange={handleRangeChange}
          style={{ width: '100%' }}
          placeholder={[
            t('pages.settlements.filters.dateFrom'),
            t('pages.settlements.filters.dateTo'),
          ]}
        />
      </Col>
      {hasActiveFilters && (
        <Col>
          <Button
            data-testid="settlement-history-filter-reset-btn"
            icon={<ClearOutlined />}
            onClick={onReset}
          >
            {t('pages.settlements.filters.resetFilters')}
          </Button>
        </Col>
      )}
    </Row>
  )
}
